import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { TemplateBlock } from "@/components/content/TemplateBlock";
import { Checklist } from "@/components/content/Checklist";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, CheckCircle2, AlertTriangle, Zap } from "lucide-react";

const faqs = [
  {
    question: "How many fields should my quote form have?",
    answer: "Keep it to 5-7 fields maximum. Every additional field reduces conversion rates. Collect essentials (name, phone, address, service) and follow up for details."
  },
  {
    question: "Should I require email or phone?",
    answer: "Phone is more valuable for lawn care—you can call immediately. Make phone required and email optional, or require both. Don't let leads slip through with email-only."
  },
  {
    question: "Do I need a CAPTCHA on my form?",
    answer: "Yes, use an invisible CAPTCHA (like reCAPTCHA v3) to prevent spam without adding friction. Visible CAPTCHAs reduce conversions."
  },
  {
    question: "What should happen after form submission?",
    answer: "Show a thank you message with expected response time. Send an auto-confirmation email. Alert yourself immediately (text notification is best)."
  }
];

const LawnCareQuoteFormTemplate = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/lawn-care-quote-form-template/";
  
  return (
    <>
      <SEO
        title="Lawn Care Quote Form Template | High-Converting Form Design"
        description="Free lawn care quote form template. Learn which fields to include and how to structure your form for maximum conversions."
        url={pageUrl}
        keywords="lawn care quote form, lawn care contact form, lawn service form template"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Lawn Care Quote Form Template"
        pageDescription="High-converting quote form template for lawn care websites."
        faqs={faqs}
      />

      <TopCTA />

      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Form Template</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Lawn Care{" "}
              <span className="text-primary">Quote Form Template</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A high-converting quote form design for lawn care websites. 
              The right fields, the right layout, maximum leads.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Recommended Form Fields
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TemplateBlock
                  title="Essential Fields (Always Include)"
                  content={`1. Full Name (text field, required)
2. Phone Number (tel field, required)
3. Email Address (email field, required or optional)
4. Service Address (text field, required)
5. Service Needed (dropdown or checkboxes)
   - Weekly Lawn Mowing
   - Bi-Weekly Lawn Mowing
   - Fertilization & Weed Control
   - Aeration & Overseeding
   - Spring/Fall Cleanup
   - Other (with text field)`}
                />
                <TemplateBlock
                  title="Optional Fields (Use Sparingly)"
                  content={`6. Estimated Lawn Size (dropdown, optional)
   - Small (under 5,000 sq ft)
   - Medium (5,000-10,000 sq ft)
   - Large (10,000-20,000 sq ft)
   - Very Large (20,000+ sq ft)
   
7. How Soon Do You Need Service? (dropdown, optional)
   - ASAP
   - Within 1 week
   - Within 2 weeks
   - Just getting quotes

8. Additional Notes (textarea, optional)`}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  Form Best Practices
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  title="Do This"
                  items={[
                    "Keep form above the fold or easily accessible",
                    "Use single-column layout for simplicity",
                    "Make button text action-oriented: 'Get My Free Quote'",
                    "Show trust signals near form (reviews, guarantee)",
                    "Include phone number as alternative contact method",
                    "Add expected response time: 'We respond within 2 hours'",
                    "Use mobile-friendly input types (tel, email)",
                    "Pre-fill city/state if you can detect location"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Common Mistakes to Avoid
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Checklist
                  title="Don't Do This"
                  items={[
                    "Don't ask for too many fields (more than 7)",
                    "Don't require details you can get later (exact sq footage)",
                    "Don't use visible CAPTCHA that adds friction",
                    "Don't bury the form at the bottom of the page",
                    "Don't use generic button text like 'Submit'",
                    "Don't forget mobile optimization",
                    "Don't skip form validation and error messages",
                    "Don't send leads to a generic inbox that's rarely checked"
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  Post-Submission Setup
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TemplateBlock
                  title="Thank You Page Content"
                  content={`Thanks for your quote request!

We've received your information and will contact you within [X hours/same business day].

What happens next:
1. We'll review your request
2. Call or text you to confirm details
3. Provide a clear, no-obligation quote

Questions? Call us now: (XXX) XXX-XXXX`}
                />
                <TemplateBlock
                  title="Auto-Reply Email"
                  content={`Subject: We received your lawn care quote request

Hi [Name],

Thanks for requesting a quote from [Company Name]!

We've received your request for [service] at [address]. A member of our team will contact you within [timeframe] to discuss your needs and provide pricing.

If you need immediate assistance, call us at (XXX) XXX-XXXX.

Looking forward to helping you with your lawn!

[Company Name]`}
                />
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      <MidPageCTA />

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">DIY vs. Done-For-You</h2>
            <DIYvsDoneForYou
              diyPoints={[
                "Build form using your website builder",
                "Set up email notifications",
                "Test thoroughly on mobile",
                "May need technical help for advanced features"
              ]}
              doneForYouPoints={[
                "Optimized form design included",
                "CRM integration available",
                "Instant text/email alerts",
                "Spam protection built-in"
              ]}
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Templates"
              links={[
                { to: "/lawn-care-quote-form/", label: "Lawn Care Quote Form Best Practices" },
                { to: "/website-fixes/quote-form-not-submitting/", label: "Fix: Quote Form Not Submitting" },
                { to: "/website-templates/lawn-care-homepage-template/", label: "Lawn Care Homepage Template" },
                { to: "/website-templates/", label: "All Templates" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <BottomCTA />
      <CalendlyEmbed />
    </>
  );
};

export default LawnCareQuoteFormTemplate;
